import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useImperativeHandle } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ELOCard from './ELOCard';
import { Button, Box, IconButton, Tooltip, Paper, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Divider } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WidgetsIcon from '@mui/icons-material/Widgets';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PaletteIcon from '@mui/icons-material/Palette';
import { outcomeModuleDisplayName } from '../utils/outcomeModuleName';

// Card dimensions
const CARD_WIDTH = 500; // Increased from 400 to accommodate longer text
const CARD_HEIGHT = 300; // Increased from 250 to accommodate longer text

// TaskColumn component to display Knowledge Tasks
function TaskColumn({ outcome, x, y, width, height, color }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        height,
        pointerEvents: 'auto',
      }}
    >
      <Paper
        elevation={3}
        style={{
          width: '100%',
          height: '100%',
          background: color ? `${color}10` : '#fff',
          border: color ? `2px solid ${color}` : '1px solid #ddd',
          borderRadius: 8,
          overflow: 'hidden',
          padding: 16,
        }}
      >
        {/* Module Header */}
        <Box sx={{ 
          p: 2, 
          background: color || '#1976d2', 
          color: '#fff',
          borderRadius: 4,
          mb: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ flex: 1 }}>
              {outcome.outcome}
            </Typography>
            
            {/* Source Type Indicator */}
            {outcome.extraction_source && (
              <Tooltip title={outcome.extraction_source === 'extracted' ? 'Extracted from document' : 'Derived by AI analysis'}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff'
                }}>
                  {outcome.extraction_source === 'extracted' ? (
                    <ContentCopyIcon sx={{ fontSize: '1rem' }} />
                  ) : (
                    <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />
                  )}
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
                    {outcome.extraction_source === 'extracted' ? 'Extracted' : 'Derived'}
                  </Typography>
                </Box>
              </Tooltip>
            )}
          </Box>
          
          {outcome.subtitle && (
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {outcome.subtitle}
            </Typography>
          )}
          {outcome.suggested_module_title && (
            <Chip 
              label={outcome.suggested_module_title} 
              size="small" 
              sx={{ 
                mt: 1, 
                background: 'rgba(255,255,255,0.2)', 
                color: '#fff',
                '& .MuiChip-label': { color: '#fff' }
              }} 
            />
          )}
        </Box>

        {/* Knowledge Tasks */}
        <Box sx={{ maxHeight: 'calc(100% - 120px)', overflow: 'auto' }}>
          {outcome.knowledge_tasks && outcome.knowledge_tasks.length > 0 ? (
            outcome.knowledge_tasks.map((task, taskIndex) => (
              <Accordion 
                key={task.code || taskIndex} 
                sx={{ 
                  mb: 1, 
                  '&:before': { display: 'none' },
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  borderRadius: 1
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    background: color ? `${color}20` : '#f8f9fa',
                    borderRadius: 1
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      {task.code}
                    </Typography>
                    <Typography variant="body2">
                      {task.title}
                    </Typography>
                    {/* Credits and Hours Display */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {(() => { 
                        const weight = Number(task.weight_pct || 0);
                        const credits = Number(task.credits || 0);
                        const notional = Number.isFinite(task.notional_hours) && task.notional_hours > 0 ? Number(task.notional_hours) : (credits * 10);
                        const inferredType = 'KT'; // Knowledge Tasks
                        const factor = 0.55; // KT factor
                        const active = Number.isFinite(task.active_hours) && task.active_hours > 0 ? Number(task.active_hours) : Math.round(notional * factor);
                        return (
                          <>
                            {weight > 0 && <Chip size="small" label={`${weight}%`} sx={{ fontSize: '0.7rem' }} />}
                            {credits > 0 && <Chip size="small" label={`${credits} cr`} sx={{ fontSize: '0.7rem' }} />}
                            {notional > 0 && <Chip size="small" label={`${notional} h n`} sx={{ fontSize: '0.7rem' }} />}
                            {active > 0 && <Chip size="small" label={`${active} h a`} sx={{ fontSize: '0.7rem' }} />}
                          </>
                        );
                      })()}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1 }}>
                  {task.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {task.description}
                    </Typography>
                  )}
                  
                  {/* Subtasks */}
                  {task.subtasks && task.subtasks.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        Subtasks:
                      </Typography>
                      {task.subtasks.map((subtask, subtaskIndex) => (
                        <Box key={subtask.code || subtaskIndex} sx={{ mb: 1, pl: 2 }}>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {subtask.code}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            {subtask.title}
                          </Typography>
                          {subtask.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {subtask.description}
                            </Typography>
                          )}
                          {subtask.weight && (
                            <Chip 
                              label={`Weight: ${subtask.weight}`} 
                              size="small" 
                              sx={{ mt: 0.5, fontSize: '0.75rem' }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No Knowledge Tasks found for this module.
            </Typography>
          )}
        </Box>

        {/* Practical Skills */}
        {outcome.practical_skills && outcome.practical_skills.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#ff8f00' }}>
              Practical Skills
            </Typography>
            <Box sx={{ maxHeight: 'calc(100% - 120px)', overflow: 'auto' }}>
              {outcome.practical_skills.map((ps, psIndex) => (
                <Accordion 
                  key={ps.code || psIndex}
                  sx={{ 
                    mb: 1, 
                    '&:before': { display: 'none' },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    borderRadius: 1,
                    border: '1px solid #ff8f00'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      background: '#fff3e0',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {ps.code}
                      </Typography>
                      <Typography variant="body2">
                        {ps.title}
                      </Typography>
                      {/* Credits and Hours Display */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {(() => { 
                          const weight = Number(ps.weight_pct || 0);
                          const credits = Number(ps.credits || 0);
                          const notional = Number.isFinite(ps.notional_hours) && ps.notional_hours > 0 ? Number(ps.notional_hours) : (credits * 10);
                          const inferredType = 'PS'; // Practical Skills
                          const factor = 0.65; // PS factor
                          const active = Number.isFinite(ps.active_hours) && ps.active_hours > 0 ? Number(ps.active_hours) : Math.round(notional * factor);
                          return (
                            <>
                              {weight > 0 && <Chip size="small" label={`${weight}%`} sx={{ fontSize: '0.7rem' }} />}
                              {credits > 0 && <Chip size="small" label={`${credits} cr`} sx={{ fontSize: '0.7rem' }} />}
                              {notional > 0 && <Chip size="small" label={`${notional} h n`} sx={{ fontSize: '0.7rem' }} />}
                              {active > 0 && <Chip size="small" label={`${active} h a`} sx={{ fontSize: '0.7rem' }} />}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1 }}>
                    {ps.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {ps.description}
                      </Typography>
                    )}
                    
                    {/* Applied Knowledge (AK) */}
                    {ps.elements && ps.elements.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Applied Knowledge:
                        </Typography>
                        {ps.elements.map((element, elementIndex) => (
                          <Box key={elementIndex} sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="primary">
                              {element}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Practical Activities (PA) */}
                    {ps.practical && ps.practical.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Practical Activities:
                        </Typography>
                        {ps.practical.map((pa, paIndex) => (
                          <Box key={paIndex} sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="secondary">
                              {pa}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Assessments (IAC) */}
                    {ps.assessments && ps.assessments.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Assessments:
                        </Typography>
                        {ps.assessments.map((assessment, assessmentIndex) => (
                          <Box key={assessmentIndex} sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {assessment}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}

        {/* Workplace Experience */}
        {outcome.workplace_experience && outcome.workplace_experience.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" sx={{ mb: 1, color: '#00897b' }}>
              Workplace Experience
            </Typography>
            <Box sx={{ maxHeight: 'calc(100% - 120px)', overflow: 'auto' }}>
              {outcome.workplace_experience.map((we, weIndex) => (
                <Accordion 
                  key={we.code || weIndex}
                  sx={{ 
                    mb: 1, 
                    '&:before': { display: 'none' },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    borderRadius: 1,
                    border: '1px solid #00897b'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      background: '#e0f2f1',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        {we.code}
                      </Typography>
                      <Typography variant="body2">
                        {we.title}
                      </Typography>
                      {/* Credits and Hours Display */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {(() => { 
                          const weight = Number(we.weight_pct || 0);
                          const credits = Number(we.credits || 0);
                          const notional = Number.isFinite(we.notional_hours) && we.notional_hours > 0 ? Number(we.notional_hours) : (credits * 10);
                          const inferredType = 'WE'; // Workplace Experience
                          const factor = 0.20; // WE factor
                          const active = Number.isFinite(we.active_hours) && we.active_hours > 0 ? Number(we.active_hours) : Math.round(notional * factor);
                          return (
                            <>
                              {weight > 0 && <Chip size="small" label={`${weight}%`} sx={{ fontSize: '0.7rem' }} />}
                              {credits > 0 && <Chip size="small" label={`${credits} cr`} sx={{ fontSize: '0.7rem' }} />}
                              {notional > 0 && <Chip size="small" label={`${notional} h n`} sx={{ fontSize: '0.7rem' }} />}
                              {active > 0 && <Chip size="small" label={`${active} h a`} sx={{ fontSize: '0.7rem' }} />}
                            </>
                          );
                        })()}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 1 }}>
                    {we.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {we.description}
                      </Typography>
                    )}
                    
                    {/* Work Activities (WA) */}
                    {we.elements && we.elements.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Work Activities:
                        </Typography>
                        {we.elements.map((element, elementIndex) => (
                          <Box key={elementIndex} sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="primary">
                              {element}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                    
                    {/* Assessments */}
                    {we.assessments && we.assessments.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                          Assessments:
                        </Typography>
                        {we.assessments.map((assessment, assessmentIndex) => (
                          <Box key={assessmentIndex} sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {assessment}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </div>
  );
}

const ZoomPanCanvas = forwardRef(function ZoomPanCanvas({ outcomes, selectedIdx, onSelectCard, onOpenColorPicker, onOpenEditModal, onUpdateModuleTitle, showTasksOnCanvas, reqNodes, readOnly = false, assignReasons = {}, qualificationId, onMarkUserAssign, onRefreshMeta, newCurriculumData }, ref) {
  // Clean up module titles by removing dots and extra formatting
  const cleanTitle = (title) => {
    if (!title) return '';
    return title
      .replace(/\.{3,}/g, '') // Remove sequences of 3+ dots
      .replace(/[.\s]+$/g, '') // Remove trailing dots and spaces
      .replace(/^[.\s]+/g, '') // Remove leading dots and spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim();
  };

  const canonicalizeCode = (type, code) => {
    const s = String(code || '').toUpperCase();
    if (type === 'WE') {
      let m = s.match(/WM-(\d{2})-WE?(\d{2})/);
      if (m) return `WM-${m[1]}-WE${m[2]}`;
      m = s.match(/\b\d{6}-\d{3}-WM-(\d{2})-(\d{2})\b/);
      if (m) return `WM-${m[1]}-WE${m[2]}`;
      m = s.match(/\bWM-(\d{2})-(\d{2})\b/);
      if (m) return `WM-${m[1]}-WE${m[2]}`;
      return s;
    }
    if (type === 'PS') {
      let m = s.match(/PM-(\d{2})-PS(\d{2})/);
      if (m) return `PM-${m[1]}-PS${m[2]}`;
      m = s.match(/\b\d{6}-\d{3}-PM-(\d{2})-(\d{2})\b/);
      if (m) return `PM-${m[1]}-PS${m[2]}`;
      m = s.match(/\bPM-(\d{2})-(\d{2})\b/);
      if (m) return `PM-${m[1]}-PS${m[2]}`;
      return s;
    }
    // KT stays as-is
    return s;
  };
  const findReasonMeta = (eloIdx, type, code) => {
    const variants = [];
    const canon = canonicalizeCode(type, code);
    variants.push(`${eloIdx}|${type}|${code}`);
    if (canon !== String(code).toUpperCase()) variants.push(`${eloIdx}|${type}|${canon}`);
    for (const k of variants) {
      if (assignReasons && assignReasons[k]) return assignReasons[k];
    }
    return null;
  };

  const findAnyReasonMeta = (type, code) => {
    const canon = canonicalizeCode(type, code);
    const keys = Object.keys(assignReasons || {});
    for (const k of keys) {
      const parts = k.split('|');
      if (parts.length !== 3) continue;
      const [, t, c] = parts;
      if (t === type && (c === String(code).toUpperCase() || c === canon)) return assignReasons[k];
    }
    return null;
  };

  const deriveTypeByCode = (code, fallbackType) => {
    const s = String(code || '').toUpperCase();
    if (/\bPM-\d{2}-(?:PS\d{2}|\d{2})\b/.test(s)) return 'PS';
    if (/\bWM-\d{2}-(?:WE\d{2}|\d{2})\b/.test(s) || /\b\d{6}-\d{3}-WM-\d{2}-\d{2}\b/.test(s)) return 'WE';
    if (/\bKM-\d{2}-KT\d{2}\b/.test(s) || /\bKT\d{4}\b/.test(s)) return 'KT';
    return fallbackType || 'KT';
  };
  const normalizeAssignmentCode = (type, code) => {
    const canonical = canonicalizeCode(type, code);
    if (canonical && canonical.length > 0) return canonical;
    const fallback = String(code || '').toUpperCase().trim();
    return fallback || '';
  };
  const mapNodeTypeToAssignmentType = (nodeType) => {
    if (!nodeType) return 'KT';
    if (nodeType === 'topic_kt') return 'KT';
    if (['practical_skill', 'practical_work'].includes(nodeType)) return 'PS';
    if (['work_experience_item', 'work_activity'].includes(nodeType)) return 'WE';
    return 'KT';
  };
  const [tool, setTool] = useState('move');
  const [cursor, setCursor] = useState('default');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const transformRef = useRef(null);
  const [kanban, setKanban] = useState({});
  const [isDraggingItem, setIsDraggingItem] = useState(false);
  const [dragOverColumn, setDragOverColumn] = useState(null); // Track which column is being dragged over
  const [unassigned, setUnassigned] = useState([]);
  const pendingMovesRef = useRef(new Map()); // Track pending moves: code -> {toCol, type, item}
  const pendingUserMarksRef = useRef(new Map()); // Track pending user marks: code -> true
  const pendingApiCallsRef = useRef(new Map()); // Debounce API calls: `${code}-${outcomeId}` -> timestamp
  const [kanbanRebuildTrigger, setKanbanRebuildTrigger] = useState(0); // Force kanban rebuild
  const [isHoverScroll, setIsHoverScroll] = useState(false);
  const dragGhostRef = useRef(null);
  const colScrollRefs = useRef({});
  const unassignedRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const mappedRef = useRef({ columns: [] });
  const scheduleRefreshMeta = useCallback(() => {
    if (!onRefreshMeta) return;
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    refreshTimerRef.current = setTimeout(async () => {
      refreshTimerRef.current = null;
      try {
        await onRefreshMeta();
      } catch (err) {

      }
    }, 750);
  }, [onRefreshMeta]);

  // Debug logging

  useEffect(() => {
    switch (tool) {
      case 'move': setCursor('grab'); break;
      case 'color': setCursor('crosshair'); break;
      default: setCursor('default');
    }
  }, [tool]);

  // Build Kanban from outcomes knowledge_tasks (KT), practical_work (PS), and workplace_experience (WE)
  useEffect(() => {
    if (!Array.isArray(outcomes) || outcomes.length === 0) return;
    

    
    // Debug logging for assignReasons structure


    // Index reqNodes by code for fast lookup
    const byCode = new Map();
    (reqNodes || []).forEach(n => { if (n && n.code) byCode.set(n.code, n); });

    const findNodeByCode = (code, typeHint) => {
      const direct = byCode.get(code);
      if (direct) return direct;
      // Fallback: match nodes whose code ends with the short code (to handle 9-digit display codes)
      const short = String(code || '').toUpperCase();
      const nodes = (reqNodes || []).filter(n => n && typeof n.code === 'string' && n.code.toUpperCase().endsWith(short));
      if (typeHint) {
        const matchType = nodes.find(n => n.node_type === typeHint);
        if (matchType) return matchType;
      }
      return nodes[0] || {};
    };
    const parentCodeMatches = (parentCode, code) => {
      if (!parentCode || !code) return false;
      const a = String(parentCode).toUpperCase();
      const b = String(code).toUpperCase();
      return a === b || a.endsWith(b) || b.endsWith(a);
    };

    const getElementsForKT = (ktCode) => {
      return (reqNodes || [])
        .filter(n => n && n.node_type === 'topic_element' && n.parent_code === ktCode)
        .map(n => `${n.code} ${n.title || ''}`.trim());
    };
    const getAssessmentsForKT = (ktCode) => {
      const out = [];
      (reqNodes || []).forEach(n => {
        if (!n || n.node_type !== 'assessment_criterion') return;
        if (n.parent_code === ktCode) {
          out.push(`${n.code} ${n.title || ''}`.trim());
          return;
        }
        if (Array.isArray(n.links) && n.links.some(l => l && l.to_code === ktCode)) {
          out.push(`${n.code} ${n.title || ''}`.trim());
        }
      });
      return out;
    };
    const getAKForPS = (psCode) => {
      return (reqNodes || [])
        .filter(n => n && n.node_type === 'applied_knowledge' && parentCodeMatches(n.parent_code, psCode))
        .map(n => `${n.code || ''} ${n.title || ''}`.trim());
    };
    const getPAForPS = (psCode) => {
      return (reqNodes || [])
        .filter(n => n && n.node_type === 'practical_activity' && n.parent_code === psCode)
        .map(n => `${n.code} ${n.title || ''}`.trim());
    };
    const getIACForPS = (psCode) => {
      const out = [];
      (reqNodes || []).forEach(n => {
        if (!n || n.node_type !== 'assessment_criterion') return;
        if (n.parent_code === psCode) {
          out.push(`${n.code} ${n.title || ''}`.trim());
          return;
        }
        if (Array.isArray(n.links) && n.links.some(l => l && l.to_code === psCode)) {
          out.push(`${n.code} ${n.title || ''}`.trim());
        }
      });
      return out;
    };
    const getWAForWE = (weCode) => {
      return (reqNodes || [])
        .filter(n => n && n.node_type === 'work_activity' && n.parent_code === weCode)
        .map(n => `${n.code} ${n.title || ''}`.trim());
    };

    const builtKanban = {};
    const assignedSet = new Set();
    const builtDebug = [];

    outcomes.forEach((o, i) => {
      const colId = String(o.id || i);
      // Allow items to be either strings (codes) or enriched objects
      const ktCodes = Array.isArray(o.knowledge_tasks) ? o.knowledge_tasks : [];
      const psCodes = Array.isArray(o.practical_work) ? o.practical_work : [];
      const weCodes = Array.isArray(o.workplace_experience) ? o.workplace_experience : [];

      // KT items
      const ktItems = ktCodes.map((code, idx) => {
        const codeStr = typeof code === 'string' ? code : (code?.code || '');
        const canonicalCode = normalizeAssignmentCode('KT', codeStr);
        const displayCode = canonicalCode || codeStr || `KT${idx + 1}`;
        const node = byCode.get(codeStr) || {};
        // Try to get enriched data from assignReasons first (includes credits from new extraction)
        const assignMeta = findReasonMeta(i, 'KT', displayCode) || {};

        // Fallback: get credits/hours from newCurriculumData if not in assignMeta
        let enrichedData = assignMeta;
        if (!enrichedData.credits && newCurriculumData) {
          // Check KM modules
          if (newCurriculumData.KM) {
            for (const module of newCurriculumData.KM) {
              if (module.topics) {
                const topic = module.topics.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
          // Check PM modules
          if (!enrichedData.credits && newCurriculumData.PM) {
            for (const module of newCurriculumData.PM) {
              if (module.subtasks) {
                const topic = module.subtasks.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
          // Check WM modules
          if (!enrichedData.credits && newCurriculumData.WM) {
            for (const module of newCurriculumData.WM) {
              if (module.subtasks) {
                const topic = module.subtasks.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
        }
        
        const title = (typeof code === 'object' && code?.title) || node.title || node.text || displayCode;
        const description = (typeof code === 'object' && code?.description) || node.text || '';
        const weight = Number((typeof code === 'object' && code?.weight_pct) || enrichedData.weight_pct || node.weight_pct || 0);
        const credits = Number((typeof code === 'object' && code?.credits) || enrichedData.credits || node.credits || 0);
        const notional = Number((typeof code === 'object' && code?.notional_hours) || enrichedData.notional_hours || node.notional_hours || (credits * 10) || 0);
        const active = Number((typeof code === 'object' && code?.active_hours) || enrichedData.active_hours || node.active_hours || 0);
        const elements = Array.isArray(code?.subtasks) && code.subtasks.length > 0
          ? code.subtasks.map(st => `${st.code || ''} ${st.title || ''}`.trim())
          : getElementsForKT(codeStr);
        const assessments = Array.isArray(code?.assessments) && code.assessments.length > 0
          ? code.assessments.map(ac => `${ac.code || ''} ${ac.title || ''}`.trim())
          : getAssessmentsForKT(codeStr);
        const normalizedForSet = canonicalCode || displayCode;
        if (normalizedForSet) assignedSet.add(normalizedForSet);
        const meta = findAnyReasonMeta('KT', normalizedForSet) || {};
        return {
          id: `${colId}-${displayCode}`,
          code: displayCode,
          type: 'KT',
          title,
          description,
          elements,
          assessments,
          weight_pct: weight,
          credits,
          notional_hours: notional,
          active_hours: active,
          source: pendingUserMarksRef.current.has(codeStr) ? 'user' : (meta.source || 'ai'),
          reason: meta.reason || '',
          confidence: meta.confidence || null
        };
      });

      // PS items
      const psItems = psCodes.map((code, idx) => {
        const codeStr = typeof code === 'string' ? code : (code?.code || '');
        const canonicalCode = normalizeAssignmentCode('PS', codeStr);
        const displayCode = canonicalCode || codeStr || `PS${idx + 1}`;
        const node = findNodeByCode(codeStr, 'practical_skill') || {};
        // Try to get enriched data from assignReasons first (includes credits from new extraction)
        const assignMeta = findReasonMeta(i, 'PS', displayCode) || {};

        // Fallback: get credits/hours from newCurriculumData if not in assignMeta
        let enrichedData = assignMeta;
        if (!enrichedData.credits && newCurriculumData) {
          // Check PM modules
          if (newCurriculumData.PM) {
            for (const module of newCurriculumData.PM) {
              if (module.subtasks) {
                const topic = module.subtasks.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
          // Check KM modules
          if (!enrichedData.credits && newCurriculumData.KM) {
            for (const module of newCurriculumData.KM) {
              if (module.topics) {
                const topic = module.topics.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
          // Check WM modules
          if (!enrichedData.credits && newCurriculumData.WM) {
            for (const module of newCurriculumData.WM) {
              if (module.subtasks) {
                const topic = module.subtasks.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
        }
        
        const title = (typeof code === 'object' && code?.title) || node.title || node.text || displayCode;
        const description = (typeof code === 'object' && code?.description) || node.text || '';
        const weight = Number((typeof code === 'object' && code?.weight_pct) || enrichedData.weight_pct || node.weight_pct || 0);
        const credits = Number((typeof code === 'object' && code?.credits) || enrichedData.credits || node.credits || 0);
        const notional = Number((typeof code === 'object' && code?.notional_hours) || enrichedData.notional_hours || node.notional_hours || (credits * 10) || 0);
        const active = Number((typeof code === 'object' && code?.active_hours) || enrichedData.active_hours || node.active_hours || 0);
        const appliedKnowledge = Array.isArray(code?.applied_knowledge) && code.applied_knowledge.length > 0
          ? code.applied_knowledge.map(ak => `${ak.code || ''} ${ak.title || ''}`.trim())
          : getAKForPS(codeStr);
        const practicalActivities = Array.isArray(code?.practical_activities) && code.practical_activities.length > 0
          ? code.practical_activities.map(pa => `${pa.code || ''} ${pa.title || ''}`.trim())
          : getPAForPS(codeStr);
        const assessments = Array.isArray(code?.assessments) && code.assessments.length > 0
          ? code.assessments.map(ac => `${ac.code || ''} ${ac.title || ''}`.trim())
          : getIACForPS(codeStr);
        const normalizedForSet = canonicalCode || displayCode;
        if (normalizedForSet) assignedSet.add(normalizedForSet);
        const meta = findAnyReasonMeta('PS', normalizedForSet) || {};
        return {
          id: `${colId}-${displayCode}`,
          code: displayCode,
          type: 'PS',
          title,
          description,
          elements: appliedKnowledge, // show AKs in elements section
          practical: practicalActivities,
          assessments,
          weight_pct: weight,
          credits,
          notional_hours: notional,
          active_hours: active,
          source: pendingUserMarksRef.current.has(codeStr) ? 'user' : (meta.source || 'ai'),
          reason: meta.reason || '',
          confidence: meta.confidence || null
        };
      });

      // WE items
      const weItems = weCodes.map((code, idx) => {
        const codeStr = typeof code === 'string' ? code : (code?.code || '');
        const canonicalCode = normalizeAssignmentCode('WE', codeStr);
        const displayCode = canonicalCode || codeStr || `WE${idx + 1}`;
        const node = findNodeByCode(codeStr, 'work_experience_item') || {};
        // Try to get enriched data from assignReasons first (includes credits from new extraction)
        const assignMeta = findReasonMeta(i, 'WE', displayCode) || {};

        // Fallback: get credits/hours from newCurriculumData if not in assignMeta
        let enrichedData = assignMeta;
        if (!enrichedData.credits && newCurriculumData) {
          // Check WM modules
          if (newCurriculumData.WM) {
            for (const module of newCurriculumData.WM) {
              if (module.subtasks) {
                const topic = module.subtasks.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
          // Check KM modules
          if (!enrichedData.credits && newCurriculumData.KM) {
            for (const module of newCurriculumData.KM) {
              if (module.topics) {
                const topic = module.topics.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
          // Check PM modules
          if (!enrichedData.credits && newCurriculumData.PM) {
            for (const module of newCurriculumData.PM) {
              if (module.subtasks) {
                const topic = module.subtasks.find(t => t.code === codeStr);
                if (topic) {
                  enrichedData = {
                    ...enrichedData,
                    credits: topic.credits || 0,
                    notional_hours: topic.notional_hours || 0,
                    weight_pct: topic.weight_pct || 0
                  };

                  break;
                }
              }
            }
          }
        }
        
        const title = (typeof code === 'object' && code?.title) || node.title || node.text || displayCode;
        const description = (typeof code === 'object' && code?.description) || node.text || '';
        const weight = Number((typeof code === 'object' && code?.weight_pct) || enrichedData.weight_pct || node.weight_pct || 0);
        const credits = Number((typeof code === 'object' && code?.credits) || enrichedData.credits || node.credits || 0);
        const notional = Number((typeof code === 'object' && code?.notional_hours) || enrichedData.notional_hours || node.notional_hours || (credits * 10) || 0);
        const active = Number((typeof code === 'object' && code?.active_hours) || enrichedData.active_hours || node.active_hours || 0);
        const activities = Array.isArray(code?.work_activities) && code.work_activities.length > 0
          ? code.work_activities.map(wa => `${wa.code || ''} ${wa.title || ''}`.trim())
          : getWAForWE(codeStr);
        const normalizedForSet = canonicalCode || displayCode;
        if (normalizedForSet) assignedSet.add(normalizedForSet);
        const meta = findAnyReasonMeta('WE', normalizedForSet) || {};
        return {
          id: `${colId}-${displayCode}`,
          code: displayCode,
          type: 'WE',
          title,
          description,
          elements: activities,
          weight_pct: weight,
          credits,
          notional_hours: notional,
          active_hours: active,
          source: pendingUserMarksRef.current.has(codeStr) ? 'user' : (meta.source || 'ai'),
          reason: meta.reason || '',
          confidence: meta.confidence || null
        };
      });

      builtKanban[colId] = { kt: ktItems, ps: psItems, we: weItems };
      const dbgEntry = {
        elo_number: o.elo_number || o.elo_no || '',
        module_title: o.suggested_module_title || '',
        kt: ktItems.map(it => ({ code: it.code, title: it.title, credits: it.credits, weight_pct: it.weight_pct })),
        ps: psItems.map(it => ({ code: it.code, title: it.title, credits: it.credits, weight_pct: it.weight_pct })),
        we: weItems.map(it => ({ code: it.code, title: it.title, credits: it.credits }))
      };
      builtDebug.push(dbgEntry);
    });

    // Debug structure for inspection: final mapped JSON used to populate columns
    try {
    } catch {}

    // Apply pending moves before setting kanban
    // This prevents items from jumping back to original positions after drop
    for (const [code, move] of pendingMovesRef.current.entries()) {
      const { toCol, type, item } = move;
      const bucket = type === 'PS' ? 'ps' : (type === 'WE' ? 'we' : 'kt');
      


      
      // Remove from ALL columns first (prevents duplication)
      let removedCount = 0;
      for (const colId of Object.keys(builtKanban)) {
        const col = builtKanban[colId];
        const ktBefore = col.kt ? col.kt.length : 0;
        const psBefore = col.ps ? col.ps.length : 0;
        const weBefore = col.we ? col.we.length : 0;
        
        if (col.kt) col.kt = col.kt.filter(i => i.code !== code);
        if (col.ps) col.ps = col.ps.filter(i => i.code !== code);
        if (col.we) col.we = col.we.filter(i => i.code !== code);
        
        const ktAfter = col.kt ? col.kt.length : 0;
        const psAfter = col.ps ? col.ps.length : 0;
        const weAfter = col.we ? col.we.length : 0;
        
        if (ktBefore !== ktAfter || psBefore !== psAfter || weBefore !== weAfter) {

          removedCount++;
        }
      }
      

      
      // Add to target column with preserved item data
      if (builtKanban[toCol] && item) {
        if (!builtKanban[toCol][bucket]) builtKanban[toCol][bucket] = [];
        const beforeAdd = builtKanban[toCol][bucket].length;
        builtKanban[toCol][bucket].push(item);
        const normalizedForSet = normalizeAssignmentCode(type, code) || code;
        if (normalizedForSet) assignedSet.add(normalizedForSet); // Mark as assigned to remove from unassigned pool

      }
    }

    setKanban(builtKanban);

    // Save normalized mapped structure for export/share consumers
    const columns = outcomes.map((o, i) => {
      const colId = String(o.id || i);
      const g = builtKanban[colId] || { kt: [], ps: [], we: [] };
      return {
        elo_number: o.elo_number || o.elo_no || `EOL${i + 1}`,
        module_title: o.suggested_module_title || '',
        knowledge_tasks: g.kt,
        practical_work: g.ps,
        workplace_experience: g.we
      };
    });
    mappedRef.current = { columns };

    // Build unassigned pool for KT, PS, and WE codes not assigned to any column (excludes child elements like PA and WA)
    const allKTs = (reqNodes || []).filter(n => n.node_type === 'topic_kt');
    const allPS = (reqNodes || []).filter(n => n.node_type === 'topic_ps');
    const allWE = (reqNodes || []).filter(n => n.node_type === 'topic_we');
    
    // Debug: Check what node types we have
    const nodeTypeCounts = {};
    (reqNodes || []).forEach(n => {
      nodeTypeCounts[n.node_type] = (nodeTypeCounts[n.node_type] || 0) + 1;
    });

    
    // Debug: Check PA codes specifically
    const paNodes = (reqNodes || []).filter(n => n.node_type === 'practical_activity');

    if (paNodes.length > 0) {

    }
    
    // Debug: Check what PS topics have as practical children
    if (allPS.length > 0) {
      const firstPS = allPS[0];
      const practicalForPS = (reqNodes || []).filter(n => n.node_type === 'practical_activity' && n.parent_code === firstPS.code);

    }
    const seenKT = new Set();
    const seenPS = new Set();
    const seenWE = new Set();
    const poolKTs = [];
    allKTs.forEach((t, idx) => {
      const assignmentType = mapNodeTypeToAssignmentType(t.node_type);
      const canonicalCode = normalizeAssignmentCode(assignmentType, t.code);
      if (!canonicalCode || assignedSet.has(canonicalCode) || seenKT.has(canonicalCode)) return;
      seenKT.add(canonicalCode);
      if (poolKTs.length === 0) {
        // No pool KTs available
      }
      poolKTs.push({
        id: `pool-KT-${canonicalCode}`,
        code: canonicalCode,
        type: 'KT',
        title: t.title || t.text || 'Topic',
        elements: (reqNodes || []).filter(n => n.node_type === 'topic_element' && n.parent_code === t.code).map(n => `${n.code} ${n.title||''}`.trim()),
        assessments: (reqNodes || []).filter(n => n.node_type === 'assessment_criterion' && n.parent_code === t.code).map(n => `${n.code} ${n.title||''}`.trim()),
        weight_pct: Number(t.weight_pct||0),
        credits: Number(t.credits||0),
        notional_hours: Number(t.notional_hours||0),
        active_hours: Number(t.active_hours||0)
      });
    });
    const poolPS = [];
    allPS.forEach(p => {
      const canonicalCode = normalizeAssignmentCode('PS', p.code);
      if (!canonicalCode || assignedSet.has(canonicalCode) || seenPS.has(canonicalCode)) return;
      seenPS.add(canonicalCode);
      poolPS.push({
        id: `pool-PS-${canonicalCode}`,
        code: canonicalCode,
        type: 'PS',
        title: p.title || p.text || 'Practical Skill',
        elements: (reqNodes || []).filter(n => n.node_type === 'applied_knowledge' && n.parent_code === p.code).map(n => `${n.code} ${n.title || ''}`.trim()),
        practical: (reqNodes || []).filter(n => n.node_type === 'practical_activity' && n.parent_code === p.code).map(n => `${n.code} ${n.title || ''}`.trim()),
        assessments: (reqNodes || []).filter(n => n.node_type === 'assessment_criterion' && n.parent_code === p.code).map(n => `${n.code} ${n.title || ''}`.trim()),
        weight_pct: Number(p.weight_pct || 0),
        credits: Number(p.credits || 0),
        notional_hours: Number(p.notional_hours || 0),
        active_hours: Number(p.active_hours || 0)
      });
    });
    const poolWE = [];
    allWE.forEach(we => {
      const canonicalCode = normalizeAssignmentCode('WE', we.code);
      if (!canonicalCode || assignedSet.has(canonicalCode) || seenWE.has(canonicalCode)) return;
      seenWE.add(canonicalCode);
      poolWE.push({
        id: `pool-WE-${canonicalCode}`,
        code: canonicalCode,
        type: 'WE',
        title: we.title || we.text || 'Work Experience',
        elements: (reqNodes || []).filter(n => n.node_type === 'work_activity' && n.parent_code === we.code).map(n => `${n.code} ${n.title || ''}`.trim()),
        assessments: (reqNodes || []).filter(n => n.node_type === 'assessment_criterion' && n.parent_code === we.code).map(n => `${n.code} ${n.title || ''}`.trim()),
        weight_pct: Number(we.weight_pct || 0),
        credits: Number(we.credits || 0),
        notional_hours: Number(we.notional_hours || 0),
        active_hours: Number(we.active_hours || 0)
      });
    });
    
    const unassignedPool = [...poolKTs, ...poolPS, ...poolWE].slice(0, 200);
    
    // Debug: Log unassigned pool stats and sample data
    if (unassignedPool.length > 0) {
      // Unassigned pool items found
    }
    
    setUnassigned(unassignedPool);
  }, [outcomes, reqNodes, kanbanRebuildTrigger]);

  // Expose mapped structure to parent (export/share)
  useImperativeHandle(ref, () => ({
    getMappedStructure: () => mappedRef.current
  }), []);

  const setColScrollRef = (id) => (el) => { if (el) colScrollRefs.current[id] = el; };
  const autoScroll = (e, el) => {
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const threshold = 40;
    if (e.clientY < rect.top + threshold) el.scrollTop -= 20;
    else if (e.clientY > rect.bottom - threshold) el.scrollTop += 20;
  };
  const createDragImage = (text) => {
    try {
      const ghost = document.createElement('div');
      ghost.style.position = 'fixed';
      ghost.style.top = '-1000px';
      ghost.style.left = '-1000px';
      ghost.style.pointerEvents = 'none';
      ghost.style.padding = '6px 10px';
      ghost.style.background = '#fff';
      ghost.style.border = '1px solid #ccc';
      ghost.style.borderRadius = '6px';
      ghost.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
      ghost.style.font = '12px sans-serif';
      ghost.textContent = text || 'Moving';
      document.body.appendChild(ghost);
      dragGhostRef.current = ghost;
      return ghost;
    } catch { return null; }
  };
  const clearDragImage = () => {
    const g = dragGhostRef.current;
    if (g && g.parentNode) g.parentNode.removeChild(g);
    dragGhostRef.current = null;
  };

  const computeItemStats = (it) => {
    const weight = Number(it.weight_pct || 0);
    const credits = Number(it.credits || 0);
    const notional = Number.isFinite(it.notional_hours) && it.notional_hours > 0 ? Number(it.notional_hours) : (credits * 10);
    const inferredType = it.type || (typeof it.code === 'string' && it.code.startsWith('PM-') ? 'PS' : (typeof it.code === 'string' && it.code.startsWith('WM-') ? 'WE' : 'KT'));
    const factor = inferredType === 'PS' ? 0.65 : (inferredType === 'WE' ? 0.20 : 0.55);
    const active = Number.isFinite(it.active_hours) && it.active_hours > 0 ? Number(it.active_hours) : Math.round(notional * factor);
    return { weight, credits, notional, active };
  };

  // Fullscreen and normal styles: scrollable whiteboard container
  // Auto fullscreen if read-only shared view
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('readonly') === '1') setIsFullscreen(true);
    } catch {}
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  const containerStyle = isFullscreen
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 2000,
        background: '#fff',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        width: '100%',
        height: '100%',
        position: 'relative',
        background: '#f0f8ff',
        overflow: 'hidden',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      };

  // Calculate card positions - ELO cards left, optional task columns per outcome to the right
  const getCardPosition = (idx) => {
    const x = 50;
    const y = idx * (CARD_HEIGHT + 80) + 100;
      return { x, y };
  };

  const getStackHeight = () => {
    const n = outcomes.length || 0;
    if (n === 0) return 1000;
    const lastY = (n - 1) * (CARD_HEIGHT + 80) + 100;
    return lastY + CARD_HEIGHT + 100; // bottom + margin
  };

  const getKanbanColumnPosition = (idx) => {
    const baseX = 50 + CARD_WIDTH + 150;
    const columnWidth = 380; // wider columns
    const x = baseX + (idx * (columnWidth + 32));
    const y = 100;
    const height = Math.max(600, getStackHeight() - y);
    return { x, y, width: columnWidth, height };
  };

  // Calculate bounding box for all cards
  const cardBBox = useMemo(() => {
    if (outcomes.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    const eloPositions = outcomes.map((_, idx) => getCardPosition(idx));
    const minX = Math.min(...eloPositions.map(p => p.x));
    const minY = Math.min(...eloPositions.map(p => p.y));
    const maxCardX = Math.max(...eloPositions.map(p => p.x + CARD_WIDTH));
    const maxCardY = Math.max(...eloPositions.map(p => p.y + CARD_HEIGHT));
    const lastKanban = getKanbanColumnPosition(Math.max(0, outcomes.length - 1));
    const maxKanbanX = lastKanban.x + lastKanban.width;
    const maxX = Math.max(maxCardX, maxKanbanX);
    const maxY = Math.max(maxCardY, lastKanban.y + lastKanban.height);
      return { minX, minY, maxX, maxY };
  }, [outcomes]);

  // Canvas style with fixed large size for infinite canvas feel
  const canvasStyle = useMemo(() => {
    const lastKanban = outcomes.length ? getKanbanColumnPosition(Math.max(0, outcomes.length - 1)) : { x: 0, width: 0 };
    const totalWidth = Math.max(50000, lastKanban.x + lastKanban.width + 1000);
    const canvasCursor = isDraggingItem ? 'grabbing' : (tool === 'move' ? 'grab' : 'default');
      return {
        width: `${totalWidth}px`,
      height: '50000px',
        position: 'relative',
      cursor: canvasCursor,
      background: '#ffffff',
      backgroundImage: 'linear-gradient(0deg, rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)',
      backgroundSize: '20px 20px',
        minWidth: '100%',
        minHeight: '100%',
      };
  }, [isDraggingItem, tool, outcomes.length]);

  // Disable fit/transform; rely on container scroll for navigation

  return (
    <div style={containerStyle}>
      {/* Tools hidden per request */}
      {/* Infinite canvas with pan & zoom */}
      <div style={{ position: 'relative', flex: 1 }}>
        {/* Fullscreen toggle */}
        <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px #0002', p: 0.5 }}>
              <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                <IconButton onClick={() => setIsFullscreen(f => !f)}>
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              </Tooltip>
            </Box>
        <TransformWrapper onInit={ref => { transformRef.current = ref; }} wheel={{ step: 0.05, disabled: isHoverScroll }} doubleClick={{ disabled: true }} panning={{ disabled: (isDraggingItem), velocityDisabled: true }} minScale={0.05} maxScale={8} limitToBounds={false} centerOnInit={false}>
            <TransformComponent>
              <div style={{ ...canvasStyle }}>
                {/* Hide standalone ELO cards; ELO info is shown above each column header now */}

                {/* Kanban columns to the right of ELO cards */}
                {outcomes.map((o, idx) => {
                  const { x, y, width, height } = getKanbanColumnPosition(idx);
                  const colId = String(o.id || idx);
                  const groups = kanban[colId] || { kt: [], ps: [], we: [] };
                  const allItems = [...(groups.kt || []), ...(groups.ps || []), ...(groups.we || [])];
                  const colTotals = (allItems || []).reduce((acc, it) => {
                    const s = computeItemStats(it);
                    acc.weight += s.weight || 0;
                    acc.credits += s.credits || 0;
                    acc.notional += s.notional || 0;
                    acc.active += s.active || 0;
                    return acc;
                  }, { weight: 0, credits: 0, notional: 0, active: 0 });

                  const handleDrop = async (e) => {
                    if (readOnly) return;
                    try {
                      const data = JSON.parse(e.dataTransfer.getData('application/json'));
                      const { itemId, fromCol, itemType, code: ddCode } = data || {};
                      if (!itemId) return;
                      // compute moving code before mutating state
                      let movingCode = ddCode || null;
                      if (fromCol === 'pool') {
                        const moving = (unassigned || []).find(it => it.id === itemId);
                        movingCode = moving?.code || null;
                      } else if (fromCol && fromCol !== colId) {
                        const src = kanban[fromCol] || { kt: [], ps: [], we: [] };
                        const found = (src.kt || []).concat(src.ps || [], src.we || []).find(it => it.id === itemId);
                        movingCode = found?.code || null;
                      }
                      if (!movingCode) return;
                      if (fromCol === 'pool') {
                        // Track this move as pending with full item data
                          const moving = (unassigned || []).find(it => it.id === itemId);
                        const t = deriveTypeByCode(movingCode, itemType);
                        if (moving) {
                          pendingMovesRef.current.set(movingCode, { 
                            toCol: colId, 
                            type: t, 
                            item: moving 
                          });
                          pendingUserMarksRef.current.set(movingCode, true);
                        }
                        
                        // Don't update kanban here - let the useEffect rebuild handle it via pending moves
                        // This prevents double-adding (once here, once in useEffect)
                        setUnassigned(prev => (prev || []).filter(it => it.id !== itemId));
                        
                        // Trigger kanban rebuild to apply pending move
                        setKanbanRebuildTrigger(prev => prev + 1);
                        
                        // persist: mark source=user for this placement
                        try {
                          // Debounce: prevent duplicate API calls within 1 second
                          const apiKey = `${movingCode}-${o.id}`;
                          const lastCall = pendingApiCallsRef.current.get(apiKey);
                          const now = Date.now();
                          if (lastCall && now - lastCall < 1000) {

                            return;
                          }
                          pendingApiCallsRef.current.set(apiKey, now);
                          
                          // Use api instance instead of fetch to ensure proper base URL and prevent duplicates
                          const api = require('../api/axios').default;
                          await api.post(`/qualifications/${qualificationId}/outcomes/${o.id}/assignment-meta`, { code: movingCode, type: t, source: 'user' });

                          scheduleRefreshMeta();
                          // Clear pending move after successful save - wait 10 seconds for backend to update
                          setTimeout(() => {
                            pendingMovesRef.current.delete(movingCode);
                            pendingUserMarksRef.current.delete(movingCode);
                            pendingApiCallsRef.current.delete(apiKey);
                          }, 10000);
                        } catch (err) {

                        }
                        // set local pill/icon immediately using any previous AI reason/confidence for this code
                        try { const prev = findAnyReasonMeta(t, movingCode) || {}; if (typeof onMarkUserAssign === 'function') onMarkUserAssign(idx, t, movingCode, prev.reason, prev.confidence); } catch {}
                        // Scheduled refreshes pick up the latest assignment metadata once persistence completes.
                        // The pending user mark keeps the pill as "User" until the timeout clears
                      } else if (fromCol && fromCol !== colId) {
                          let moving = null;
                        // Find the moving item first
                        const src = kanban[fromCol] || { kt: [], ps: [], we: [] };
                          ['kt','ps','we'].forEach(b => {
                          const found = (src[b] || []).find(it => it.id === itemId);
                          if (found) moving = found;
                          });
                        
                        // Track this move as pending with full item data
                        const t = deriveTypeByCode(movingCode, itemType || 'KT');
                          if (moving) {
                          pendingMovesRef.current.set(movingCode, { 
                            toCol: colId, 
                            type: t, 
                            item: moving 
                          });
                          pendingUserMarksRef.current.set(movingCode, true);
                        }
                        
                        // Don't update kanban here - let the useEffect rebuild handle it via pending moves
                        // This prevents double-adding (once here, once in useEffect)
                        
                        // Trigger kanban rebuild to apply pending move
                        setKanbanRebuildTrigger(prev => prev + 1);
                        
                        // persist: mark source=user for this placement
                        // Find the target outcome by colId
                        try {
                          const targetOutcome = (outcomes || []).find(oc => String(oc.id) === String(colId));
                          if (targetOutcome) {
                            // Immediate save with confirmation - no debouncing for user-initiated moves
                            const apiKey = `${movingCode}-${targetOutcome.id}`;
                            const api = require('../api/axios').default;
                            
                            // Optimistic update: mark as saving
                            pendingApiCallsRef.current.set(apiKey, Date.now());
                            
                            try {
                              // Immediate API call with confirmation
                              const response = await api.post(
                                `/qualifications/${qualificationId}/outcomes/${targetOutcome.id}/assignment-meta`, 
                                { code: movingCode, type: t, source: 'user' }
                              );
                              
                              // Verify response confirms save
                              if (response.data && response.data.success) {
                                // Save confirmed - verify persistence
                                try {
                                  // Immediately verify by fetching the outcome
                                  const verifyResponse = await api.get(`/qualifications/${qualificationId}/outcomes/${targetOutcome.id}`);
                                  const verifiedOutcome = verifyResponse.data;
                                  const isAssigned = (
                                    (Array.isArray(verifiedOutcome.knowledge_tasks) && verifiedOutcome.knowledge_tasks.includes(movingCode)) ||
                                    (Array.isArray(verifiedOutcome.practical_work) && verifiedOutcome.practical_work.includes(movingCode)) ||
                                    (Array.isArray(verifiedOutcome.workplace_experience) && verifiedOutcome.workplace_experience.includes(movingCode))
                                  );
                                  
                                  if (isAssigned) {
                                    // Persistence verified - clear pending moves immediately
                                    pendingMovesRef.current.delete(movingCode);
                                    pendingUserMarksRef.current.delete(movingCode);
                                    pendingApiCallsRef.current.delete(apiKey);
                                    scheduleRefreshMeta();
                                  } else {
                                    // Persistence verification failed - retry save
                                    console.warn(`[ZoomPanCanvas] Persistence verification failed for ${movingCode}, retrying...`);
                                    const retryResponse = await api.post(
                                      `/qualifications/${qualificationId}/outcomes/${targetOutcome.id}/assignment-meta`, 
                                      { code: movingCode, type: t, source: 'user' }
                                    );
                                    if (retryResponse.data && retryResponse.data.success) {
                                      pendingMovesRef.current.delete(movingCode);
                                      pendingUserMarksRef.current.delete(movingCode);
                                      pendingApiCallsRef.current.delete(apiKey);
                                      scheduleRefreshMeta();
                                    }
                                  }
                                } catch (verifyErr) {
                                  console.error('[ZoomPanCanvas] Error verifying persistence:', verifyErr);
                                  // Still clear pending moves if initial save succeeded
                                  pendingMovesRef.current.delete(movingCode);
                                  pendingUserMarksRef.current.delete(movingCode);
                                  pendingApiCallsRef.current.delete(apiKey);
                                  scheduleRefreshMeta();
                                }
                              } else {
                                // Save failed - keep pending move for retry
                                console.error('[ZoomPanCanvas] Save failed - response not successful');
                                pendingApiCallsRef.current.delete(apiKey);
                              }
                            } catch (saveErr) {
                              // Save failed - rollback optimistic update
                              console.error('[ZoomPanCanvas] Error saving assignment:', saveErr);
                              pendingApiCallsRef.current.delete(apiKey);
                              // Optionally show error to user
                              if (saveErr.response?.data?.message) {
                                console.error('[ZoomPanCanvas] Save error:', saveErr.response.data.message);
                              }
                            }
                          }
                        } catch (err) {
                          console.error('[ZoomPanCanvas] Error in drag-and-drop save:', err);
                        }
                        // set local pill/icon immediately using any previous AI reason/confidence for this code
                        try { 
                          const targetOutcomeIdx = (outcomes || []).findIndex(oc => String(oc.id) === String(colId));
                          if (targetOutcomeIdx >= 0) {
                            const prev = findAnyReasonMeta(t, movingCode) || {}; 
                            if (typeof onMarkUserAssign === 'function') onMarkUserAssign(targetOutcomeIdx, t, movingCode, prev.reason, prev.confidence);
                          }
                        } catch {}
                        // Scheduled refreshes pick up the latest assignment metadata once persistence completes.
                        // The pending user mark keeps the pill as "User" until the timeout clears
                      }
                      // ensure drag state reset to re-enable pan/scroll
                      try { setIsDraggingItem(false); } catch {}
                    } catch {}
                  };

                  return (
                    <div
                      key={`kanban-${colId}`}
                      style={{ position: 'absolute', left: x, top: y, width, height, pointerEvents: 'auto' }}
                      onDragOver={(e) => { 
                        e.preventDefault(); 
                        setDragOverColumn(colId);
                        try { e.dataTransfer.dropEffect = 'move'; } catch {} 
                      }}
                      onDragLeave={(e) => {
                        // Only clear if leaving the column container itself, not child elements
                        if (e.currentTarget === e.target) {
                          setDragOverColumn(null);
                        }
                      }}
                      onDrop={(e) => {
                        e.stopPropagation(); // Prevent event bubbling to avoid duplicate calls
                        setDragOverColumn(null);
                        handleDrop(e);
                      }}
                    >
                      <Paper 
                        elevation={dragOverColumn === colId ? 8 : 3} 
                        sx={{ 
                          width: '100%', 
                          height: '100%', 
                          display: 'flex', 
                          flexDirection: 'column', 
                          border: dragOverColumn === colId 
                            ? '3px solid #1976d2' 
                            : (o.color ? `2px solid ${o.color}` : '1px solid #ddd'), 
                          background: dragOverColumn === colId 
                            ? '#e3f2fd' 
                            : (o.color ? `${o.color}0A` : '#fff'), 
                          borderRadius: 1, 
                          overflow: 'hidden', 
                          position: 'relative',
                          transition: 'all 0.2s ease'
                        }} 
                        onWheel={(e)=>e.stopPropagation()} 
                        onMouseDown={(e)=>e.stopPropagation()} 
                        onTouchStart={(e)=>e.stopPropagation()}
                      >
                        {/* Column header with module name, totals, and controls */}
                        <Box sx={{ p: 1.25, background: o.color || '#1976d2', color: '#fff' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                              {outcomeModuleDisplayName(o, idx)}
                            </Typography>
                            {!readOnly && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip title="Change column color">
                                  <IconButton size="small" onClick={() => { onSelectCard(idx); onOpenColorPicker(); }} sx={{ color: '#fff' }}>
                                    <PaletteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit module title">
                                  <IconButton size="small" onClick={() => { onSelectCard(idx); onOpenEditModal(idx); }} sx={{ color: '#fff' }}>
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip size="small" sx={{ color: '#0d47a1', backgroundColor: 'rgba(144,202,249,0.95)', fontWeight: 800 }} label={`Σ ${Math.round(colTotals.credits)} cr`} />
                            <Chip size="small" sx={{ color: '#4a148c', backgroundColor: 'rgba(206,147,216,0.95)', fontWeight: 800 }} label={`Σ ${Math.round(colTotals.notional)} h n`} />
                            <Chip size="small" sx={{ color: '#1b5e20', backgroundColor: 'rgba(165,214,167,0.95)', fontWeight: 800 }} label={`Σ ${Math.round(colTotals.active)} h a`} />
                          </Box>
                        </Box>
                        <Box ref={setColScrollRef(colId)} sx={{ flex: 1, overflow: 'auto', p: 1, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', '&::-webkit-scrollbar': { width: 10 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#bbb', borderRadius: 8 }, '&::-webkit-scrollbar-track': { backgroundColor: '#eee' } }} onMouseEnter={() => setIsHoverScroll(true)} onMouseLeave={() => setIsHoverScroll(false)} onDragOver={(e)=>{ e.preventDefault(); autoScroll(e, colScrollRefs.current[colId]); }} onDrop={handleDrop}>
                          {/* Knowledge Tasks */}
                          {(groups.kt || []).map(it => (
                            <Paper
                              key={it.id}
                              sx={{ p: 0, mb: 1, border: '2px solid #1565c0', borderLeft: '8px solid #0d47a1', borderRadius: 1, background: '#bbdefb', overflow: 'hidden' }}
                            >
                              <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  sx={{ px: 1, py: 0.5, cursor: (isDraggingItem ? 'grabbing' : 'grab') }}
                                  draggable={!readOnly}
                                  onDragStart={(e) => {
                                    if (readOnly) return;
                                    setIsDraggingItem(true);
                                    e.stopPropagation();
                                    e.dataTransfer.setData('application/json', JSON.stringify({ itemId: it.id, fromCol: colId, itemType: it.type || 'KT', code: it.code }));
                                    e.dataTransfer.effectAllowed = 'move';
                                    const ghost = createDragImage(`${it.code} • ${it.title}`);
                                    if (ghost && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(ghost, 10, 10);
                                  }}
                                  onDragEnd={() => { setIsDraggingItem(false); setDragOverColumn(null); clearDragImage(); }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%', position: 'relative' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>{it.code || 'KTxxxx'}</Typography>
                                    <Typography variant="body2">{it.title}</Typography>
                                    {(() => { const meta = findReasonMeta(idx, 'KT', it.code); const isUser = !!(meta && meta.source === 'user'); const label = isUser ? 'User' : 'AI'; return (
                                      <Chip size="small" label={label} sx={{ position: 'absolute', top: 4, right: 4, height: 20, fontWeight: 700, backgroundColor: isUser ? '#fff3e0' : '#e3f2fd', color: isUser ? '#e65100' : '#0d47a1' }} />
                                    ); })()}
                                    {(() => { const meta = findReasonMeta(idx, 'KT', it.code); if (!meta || (!meta.reason && !meta.confidence)) return null; const pct = (typeof meta.confidence === 'number') ? Math.round(meta.confidence * 100) : null; return (<Tooltip title={`${meta.reason || ''}${pct!=null ? ` (confidence: ${pct}%)` : ''}`.trim()}><InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /></Tooltip>); })()}
                                    {/* Metrics bar */}
                                    {(() => { const s = computeItemStats(it); const meta = findReasonMeta(idx, 'KT', it.code); const pct = (meta && typeof meta.confidence === 'number') ? Math.round(meta.confidence * 100) : null; return (
                                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, borderTop: '1px solid #eee', pt: 0.5, alignItems: 'center' }}>
                                        {/* Hide item percentage */}
                                        <Chip size="small" label={`${s.credits} cr`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#90caf9', color: '#0d47a1' }} />
                                        <Chip size="small" label={`${s.notional} h n`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#ce93d8', color: '#4a148c' }} />
                                        <Chip size="small" label={`${s.active} h a`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#a5d6a7', color: '#1b5e20' }} />
                                        {meta && (meta.reason || pct!=null) ? (
                                          <Tooltip title={`${meta.reason || ''}${pct!=null ? ` (confidence: ${pct}%)` : ''}`.trim()}>
                                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
                                          </Tooltip>
                                        ) : <span />}
                                      </Box>
                                    ); })()}
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                                  {/* Elements */}
                                  {Array.isArray(it.elements) && it.elements.length > 0 && (
                                    <Box sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#0d47a1', display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#e3f2fd', px: 1, py: 0.25, borderRadius: 1 }}>
                                        <WidgetsIcon sx={{ fontSize: 16, color: '#0d47a1' }} /> Elements
                                      </Typography>
                                      {it.elements.map((el, idxEl) => (
                                        <Box key={`${it.id}-el-${idxEl}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.5, borderLeft: '3px solid #42a5f5', backgroundColor: '#e3f2fd55', borderRadius: 1 }}>
                                          <WidgetsIcon sx={{ fontSize: 16, color: '#1e88e5' }} />
                                          <Typography variant="body2" sx={{ color: '#0d47a1', fontWeight: 600 }}>{el}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                  {/* Assessments */}
                                  {Array.isArray(it.assessments) && it.assessments.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#b71c1c', display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#ffebee', px: 1, py: 0.25, borderRadius: 1 }}>
                                        <FactCheckIcon sx={{ fontSize: 16, color: '#b71c1c' }} /> Assessments
                                      </Typography>
                                      {it.assessments.map((ac, idxAc) => (
                                        <Box key={`${it.id}-ac-${idxAc}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.5, borderLeft: '3px solid #ef5350', backgroundColor: '#ffebee80', borderRadius: 1 }}>
                                          <FactCheckIcon sx={{ fontSize: 16, color: '#e53935' }} />
                                          <Typography variant="body2" sx={{ color: '#b71c1c', fontWeight: 600 }}>{ac}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            </Paper>
                          ))}
                          {/* Practical Skills */}
                          {(groups.ps || []).map(it => (
                            <Paper key={it.id} sx={{ p: 0, mb: 1, border: '2px solid #ef6c00', borderLeft: '8px solid #e65100', borderRadius: 1, background: '#ffe0b2', overflow: 'hidden' }}>
                              <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, py: 0.5, cursor: (isDraggingItem ? 'grabbing' : 'grab') }} draggable={!readOnly} onDragStart={(e) => { if (readOnly) return; setIsDraggingItem(true); e.stopPropagation(); e.dataTransfer.setData('application/json', JSON.stringify({ itemId: it.id, fromCol: colId, itemType: it.type || 'PS', code: it.code })); e.dataTransfer.effectAllowed = 'move'; const ghost = createDragImage(`${it.code} • ${it.title}`); if (ghost && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(ghost, 10, 10); }} onDragEnd={() => { setIsDraggingItem(false); setDragOverColumn(null); clearDragImage(); }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%', position: 'relative' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>{it.code || 'PS'}</Typography>
                                    <Typography variant="body2">{it.title}</Typography>
                                    {(() => { const meta = findReasonMeta(idx, 'PS', it.code); const isUser = !!(meta && meta.source === 'user'); const label = isUser ? 'User' : 'AI'; return (
                                      <Chip size="small" label={label} sx={{ position: 'absolute', top: 4, right: 4, height: 20, fontWeight: 700, backgroundColor: isUser ? '#fff3e0' : '#e3f2fd', color: isUser ? '#e65100' : '#0d47a1' }} />
                                    ); })()}
                                    {(() => { const meta = findReasonMeta(idx, 'PS', it.code); if (!meta || (!meta.reason && !meta.confidence)) return null; const pct = (typeof meta.confidence === 'number') ? Math.round(meta.confidence * 100) : null; return (
                                      <Tooltip title={`${meta.reason || ''}${pct!=null ? ` (confidence: ${pct}%)` : ''}`.trim()}>
                                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                      </Tooltip>
                                    ); })()}
                                    {/* Metrics bar */}
                                    {(() => { const s = computeItemStats(it); const meta = findReasonMeta(idx, 'PS', it.code); const pct = (meta && typeof meta.confidence === 'number') ? Math.round(meta.confidence * 100) : null; return (
                                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, borderTop: '1px solid #eee', pt: 0.5, alignItems: 'center' }}>
                                        {/* Hide item percentage */}
                                        <Chip size="small" label={`${s.credits} cr`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#90caf9', color: '#0d47a1' }} />
                                        <Chip size="small" label={`${s.notional} h n`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#ce93d8', color: '#4a148c' }} />
                                        <Chip size="small" label={`${s.active} h a`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#a5d6a7', color: '#1b5e20' }} />
                                        {meta && (meta.reason || pct!=null) ? (
                                          <Tooltip title={`${meta.reason || ''}${pct!=null ? ` (confidence: ${pct}%)` : ''}`.trim()}>
                                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
                                          </Tooltip>
                                        ) : <span />}
                                      </Box>
                                    ); })()}
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                                  {/* Applied Knowledge */}
                                  {Array.isArray(it.elements) && it.elements.length > 0 && (
                                    <Box sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#0d47a1', display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#e3f2fd', px: 1, py: 0.25, borderRadius: 1 }}>
                                        <WidgetsIcon sx={{ fontSize: 16, color: '#0d47a1' }} /> Applied Knowledge
                                      </Typography>
                                      {it.elements.map((el, idxEl) => (
                                        <Box key={`${it.id}-ak-${idxEl}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.5, borderLeft: '3px solid #42a5f5', backgroundColor: '#e3f2fd55', borderRadius: 1 }}>
                                          <WidgetsIcon sx={{ fontSize: 16, color: '#1e88e5' }} />
                                          <Typography variant="body2" sx={{ color: '#0d47a1', fontWeight: 600 }}>{el}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                  {/* Practical Activities */}
                                  {Array.isArray(it.practical) && it.practical.length > 0 && (
                                    <Box sx={{ mb: 0.5 }}>
                                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#b71c1c', display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#ffebee', px: 1, py: 0.25, borderRadius: 1 }}>
                                        <FactCheckIcon sx={{ fontSize: 16, color: '#b71c1c' }} /> Practical Activities
                                      </Typography>
                                      {it.practical.map((pa, idxPa) => (
                                        <Box key={`${it.id}-pa-${idxPa}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.5, borderLeft: '3px solid #ef5350', backgroundColor: '#ffebee80', borderRadius: 1 }}>
                                          <FactCheckIcon sx={{ fontSize: 16, color: '#e53935' }} />
                                          <Typography variant="body2" sx={{ color: '#b71c1c', fontWeight: 600 }}>{pa}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                  {/* Assessments */}
                                  {Array.isArray(it.assessments) && it.assessments.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#6a1b9a', display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#f3e5f5', px: 1, py: 0.25, borderRadius: 1 }}>
                                        <FactCheckIcon sx={{ fontSize: 16, color: '#6a1b9a' }} /> Assessments
                                      </Typography>
                                      {it.assessments.map((ac, idxAc) => (
                                        <Box key={`${it.id}-psac-${idxAc}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.5, borderLeft: '3px solid #ab47bc', backgroundColor: '#f3e5f580', borderRadius: 1 }}>
                                          <FactCheckIcon sx={{ fontSize: 16, color: '#8e24aa' }} />
                                          <Typography variant="body2" sx={{ color: '#6a1b9a', fontWeight: 600 }}>{ac}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            </Paper>
                          ))}
                          {/* Workplace Experience */}
                          {(groups.we || []).map(it => (
                            <Paper key={it.id} sx={{ p: 0, mb: 1, border: '2px solid #2e7d32', borderLeft: '8px solid #1b5e20', borderRadius: 1, background: '#c8e6c9', overflow: 'hidden' }}>
                              <Accordion disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, py: 0.5, cursor: (isDraggingItem ? 'grabbing' : 'grab') }} draggable={!readOnly} onDragStart={(e) => { if (readOnly) return; setIsDraggingItem(true); e.stopPropagation(); e.dataTransfer.setData('application/json', JSON.stringify({ itemId: it.id, fromCol: colId, itemType: it.type || 'WE', code: it.code })); e.dataTransfer.effectAllowed = 'move'; const ghost = createDragImage(`${it.code} • ${it.title}`); if (ghost && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(ghost, 10, 10); }} onDragEnd={() => { setIsDraggingItem(false); setDragOverColumn(null); clearDragImage(); }}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%', position: 'relative' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.3 }}>{it.code || 'WE'}</Typography>
                                    <Typography variant="body2">{it.title}</Typography>
                                    {(() => { const meta = findReasonMeta(idx, 'WE', it.code); const isUser = !!(meta && meta.source === 'user'); const label = isUser ? 'User' : 'AI'; return (
                                      <Chip size="small" label={label} sx={{ position: 'absolute', top: 4, right: 4, height: 20, fontWeight: 700, backgroundColor: isUser ? '#fff3e0' : '#e3f2fd', color: isUser ? '#e65100' : '#0d47a1' }} />
                                    ); })()}
                                    {(() => { const meta = findReasonMeta(idx, 'WE', it.code); if (!meta || (!meta.reason && !meta.confidence)) return null; const pct = (typeof meta.confidence === 'number') ? Math.round(meta.confidence * 100) : null; return (
                                      <Tooltip title={`${meta.reason || ''}${pct!=null ? ` (confidence: ${pct}%)` : ''}`.trim()}>
                                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                      </Tooltip>
                                    ); })()}
                                    {/* Metrics bar */}
                                    {(() => { const s = computeItemStats(it); const meta = findReasonMeta(idx, 'WE', it.code); const pct = (meta && typeof meta.confidence === 'number') ? Math.round(meta.confidence * 100) : null; return (
                                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, borderTop: '1px solid #eee', pt: 0.5, alignItems: 'center' }}>
                                        {/* Hide item percentage */}
                                        <Chip size="small" label={`${s.credits} cr`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#90caf9', color: '#0d47a1' }} />
                                        <Chip size="small" label={`${s.notional} h n`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#ce93d8', color: '#4a148c' }} />
                                        <Chip size="small" label={`${s.active} h a`} sx={{ height: 22, fontWeight: 700, backgroundColor: '#a5d6a7', color: '#1b5e20' }} />
                                        {meta && (meta.reason || pct!=null) ? (
                                          <Tooltip title={`${meta.reason || ''}${pct!=null ? ` (confidence: ${pct}%)` : ''}`.trim()}>
                                            <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
                                          </Tooltip>
                                        ) : <span />}
                                      </Box>
                                    ); })()}
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0, px: 1, pb: 1 }}>
                                  {/* Work Activities */}
                                  {Array.isArray(it.elements) && it.elements.length > 0 && (
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#0d47a1', display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#e3f2fd', px: 1, py: 0.25, borderRadius: 1 }}>
                                        <WidgetsIcon sx={{ fontSize: 16, color: '#0d47a1' }} /> Work Activities
                                      </Typography>
                                      {it.elements.map((wa, idxWa) => (
                                        <Box key={`${it.id}-wa-${idxWa}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 2, py: 0.5, borderLeft: '3px solid #42a5f5', backgroundColor: '#e3f2fd55', borderRadius: 1 }}>
                                          <WidgetsIcon sx={{ fontSize: 16, color: '#1e88e5' }} />
                                          <Typography variant="body2" sx={{ color: '#0d47a1', fontWeight: 600 }}>{wa}</Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            </Paper>
                          ))}
                        </Box>
                        {/* Column totals */}
                        <Box sx={{ p: 1, borderTop: '1px solid #ddd', background: '#fafafa' }}>
                          {(() => {
                            const totals = (allItems || []).reduce((acc, it) => {
                              const s = computeItemStats(it);
                              acc.weight += s.weight || 0;
                              acc.credits += s.credits || 0;
                              acc.notional += s.notional || 0;
                              acc.active += s.active || 0;
                              return acc;
                            }, { weight: 0, credits: 0, notional: 0, active: 0 });
                            return (
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {/* Hide percentage sum */}
                                <Chip size="small" color="default" label={`Σ ${Math.round(totals.credits)} cr`} />
                                <Chip size="small" color="default" label={`Σ ${Math.round(totals.notional)} h n`} />
                                <Chip size="small" color="default" label={`Σ ${Math.round(totals.active)} h a`} />
                              </Box>
                            );
                          })()}
                        </Box>
                      </Paper>
                    </div>
                  );
                })}
                {/* Right-side unassigned pool */}
                {!readOnly && (
                <div style={{ position: 'absolute', top: 100, left: (getKanbanColumnPosition(Math.max(0, outcomes.length-1)).x + getKanbanColumnPosition(Math.max(0, outcomes.length-1)).width + 40), width: 360, height: Math.max(600, getStackHeight()-100), pointerEvents: 'auto' }} onDragOver={e=>{e.preventDefault();}}>
                  <Paper elevation={3} sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid #ddd', background: '#fff', borderRadius: 1, overflow: 'hidden' }} onWheel={(e)=>e.stopPropagation()}>
                    <Box sx={{ p: 1.25, background: '#263238', color: '#fff' }}>
                      {(() => {
                        const total = (unassigned || []).length;
                        const byType = (unassigned || []).reduce((acc, it) => { acc[it.type] = (acc[it.type]||0)+1; return acc; }, {});
                        return (
                          <>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                              Unassigned ({total})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                              <Chip size="small" sx={{ color: '#0d47a1', backgroundColor: 'rgba(144,202,249,0.95)', fontWeight: 800 }} label={`KT ${byType.KT||0}`} />
                              <Chip size="small" sx={{ color: '#e65100', backgroundColor: 'rgba(255,204,128,0.95)', fontWeight: 800 }} label={`PS ${byType.PS||0}`} />
                              <Chip size="small" sx={{ color: '#1b5e20', backgroundColor: 'rgba(165,214,167,0.95)', fontWeight: 800 }} label={`WE ${byType.WE||0}`} />
                            </Box>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>Drag KT/PS/WE into a column</Typography>
                          </>
                        );
                      })()}
                    </Box>
                    <Box ref={unassignedRef} sx={{ flex: 1, overflow: 'auto', p: 1, overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', '&::-webkit-scrollbar': { width: 10 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#bbb', borderRadius: 8 }, '&::-webkit-scrollbar-track': { backgroundColor: '#eee' } }} onMouseEnter={() => setIsHoverScroll(true)} onMouseLeave={() => setIsHoverScroll(false)} onDragOver={(e)=>{ e.preventDefault(); autoScroll(e, unassignedRef.current); }}>
                      {unassigned.map(it => {
                        // Get children for this item (return objects with code and title)
                        const children = (() => {
                          if (it.type === 'KT') {
                            const elements = (reqNodes || [])
                              .filter(n => n && n.node_type === 'topic_element' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'element' }));
                            const iacs = (reqNodes || [])
                              .filter(n => n && n.node_type === 'assessment_criterion' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'iac' }));
                            return [...elements, ...iacs];
                          }
                          if (it.type === 'PS') {
                            const pa = (reqNodes || [])
                              .filter(n => n && n.node_type === 'practical_activity' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'pa' }));
                            const ak = (reqNodes || [])
                              .filter(n => n && n.node_type === 'applied_knowledge' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'ak' }));
                            const iacs = (reqNodes || [])
                              .filter(n => n && n.node_type === 'assessment_criterion' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'iac' }));
                            return [...pa, ...ak, ...iacs];
                          }
                          if (it.type === 'WE') {
                            const wa = (reqNodes || [])
                              .filter(n => n && n.node_type === 'work_activity' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'wa' }));
                            const iacs = (reqNodes || [])
                              .filter(n => n && n.node_type === 'assessment_criterion' && n.parent_code === it.code)
                              .map(n => ({ code: n.code, title: n.title || '', type: 'iac' }));
                            return [...wa, ...iacs];
                          }
                          return [];
                        })();
                        
                        const stats = computeItemStats(it);
                        
                        return (
                          <Accordion
                            key={it.id}
                            draggable
                            onDragStart={(e)=>{ 
                              e.stopPropagation(); // Prevent canvas pan
                              setIsDraggingItem(true); 
                              e.dataTransfer.setData('application/json', JSON.stringify({ itemId: it.id, fromCol: 'pool', itemType: it.type || 'KT', code: it.code })); 
                              e.dataTransfer.effectAllowed = 'move'; 
                              const ghost = createDragImage(`${it.code} • ${it.title}`); 
                              if (ghost && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(ghost, 10, 10); 
                            }} 
                            onDragEnd={()=>{ 
                              setIsDraggingItem(false); 
                              setDragOverColumn(null); 
                              clearDragImage(); 
                            }} 
                            onMouseDown={(e) => e.stopPropagation()} // Prevent canvas pan on mousedown
                            sx={{ 
                              mb: 1, 
                              '&:before': { display: 'none' },
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              borderRadius: 1,
                              cursor: 'grab',
                              '&:active': { cursor: 'grabbing' }
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<ExpandMoreIcon />}
                              sx={{ 
                                background: '#f8f9fa',
                                borderRadius: 1,
                                '&:hover': { background: '#e9ecef' }
                              }}
                            >
                              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Typography variant="subtitle2" fontWeight="bold" color="primary">
                                  {it.code}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {it.title}
                                </Typography>
                                {/* Credits and Hours Display (no weight % for unassigned) */}
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  <Chip size="small" label={`${stats.credits.toFixed(2)} cr`} sx={{ height: 20, fontSize: '0.7rem' }} />
                                  <Chip size="small" label={`${Math.round(stats.notional)} h n`} sx={{ height: 20, fontSize: '0.7rem' }} />
                                  <Chip size="small" label={`${Math.round(stats.active)} h a`} sx={{ height: 20, fontSize: '0.7rem' }} />
                                </Box>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                              {children.length > 0 ? (
                                <Box>
                                  {/* Group children by type */}
                                  {it.type === 'KT' && (
                                    <>
                                      {children.filter(c => c.type === 'element').length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Elements ({children.filter(c => c.type === 'element').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'element').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                      {children.filter(c => c.type === 'iac').length > 0 && (
                                        <Box>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Assessments ({children.filter(c => c.type === 'iac').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'iac').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  {it.type === 'PS' && (
                                    <>
                                      {children.filter(c => c.type === 'pa').length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Practical Activities ({children.filter(c => c.type === 'pa').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'pa').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                          </Box>
                                          ))}
                                        </Box>
                                      )}
                                      {children.filter(c => c.type === 'ak').length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Applied Knowledge ({children.filter(c => c.type === 'ak').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'ak').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                      {children.filter(c => c.type === 'iac').length > 0 && (
                                        <Box>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Assessments ({children.filter(c => c.type === 'iac').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'iac').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                  {it.type === 'WE' && (
                                    <>
                                      {children.filter(c => c.type === 'wa').length > 0 && (
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Work Activities ({children.filter(c => c.type === 'wa').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'wa').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                      {children.filter(c => c.type === 'iac').length > 0 && (
                                        <Box>
                                          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                            Assessments ({children.filter(c => c.type === 'iac').length}):
                                          </Typography>
                                          {children.filter(c => c.type === 'iac').map((child, idx) => (
                                            <Box key={child.code || idx} sx={{ mb: 1, pl: 2 }}>
                                              <Typography variant="body2" color="primary" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                                {child.code}
                                              </Typography>
                                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                {child.title}
                                              </Typography>
                                            </Box>
                                          ))}
                                        </Box>
                                      )}
                                    </>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                  No child elements found
                                </Typography>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  </Paper>
                </div>
                )}
              </div>
            </TransformComponent>
      </TransformWrapper>
      </div>
    </div>
  );
});

export default ZoomPanCanvas;